import React, { Component } from "react";
import "bootstrap/dist/css/bootstrap.css";
import "./Homelab.css";

// Same-origin paths, proxied by Nginx to Uptime Kuma's public status-page API.
// Monitors come from whatever is published on the "homelab" status page.
const STATUS_PAGE_URL = "/kuma-api/status-page/homelab";
const HEARTBEAT_URL = "/kuma-api/status-page/heartbeat/homelab";
const REFRESH_MS = 60000;

// Uptime Kuma heartbeat status codes (src/util.ts: DOWN, UP, PENDING, MAINTENANCE).
const STATUS_LABELS = {
  0: { label: "Down", modifier: "down" },
  1: { label: "Operational", modifier: "up" },
  2: { label: "Pending", modifier: "pending" },
  3: { label: "Maintenance", modifier: "maintenance" },
};

async function fetchJson(url, signal) {
  const response = await fetch(url, { signal, headers: { Accept: "application/json" } });
  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }
  return response.json();
}

// Combine the published monitor list with each monitor's latest heartbeat.
// Only the name, status and uptime are kept; nothing else is rendered.
function buildMonitors(statusPage, heartbeats) {
  const groups = statusPage.publicGroupList || [];
  const heartbeatList = heartbeats.heartbeatList || {};
  const uptimeList = heartbeats.uptimeList || {};

  return groups.flatMap((group) =>
    (group.monitorList || []).map((monitor) => {
      const beats = heartbeatList[monitor.id] || [];
      // Kuma returns heartbeats oldest-first, so the latest is last.
      const latest = beats[beats.length - 1];
      const uptime = uptimeList[`${monitor.id}_24`];

      return {
        id: monitor.id,
        name: String(monitor.name || "").replace(/\s+/g, " ").trim(),
        status: latest ? STATUS_LABELS[latest.status] : undefined,
        uptime: typeof uptime === "number" ? uptime : null,
      };
    })
  );
}

function formatUptime(uptime) {
  const percent = uptime * 100;
  return `${percent === 100 ? "100" : percent.toFixed(2)}%`;
}

function SystemStatusCard({ monitor }) {
  const status = monitor.status || { label: "No data", modifier: "unknown" };

  return (
    <div className="Card HomelabCard">
      <h3 className="HomelabCardTitle">{monitor.name}</h3>
      <div className="HomelabStatusWrap">
        <p className={`HomelabStatus HomelabStatus--${status.modifier}`}>
          <span className="HomelabStatusDot" aria-hidden="true" />
          {status.label}
        </p>
        {monitor.uptime !== null && (
          <p className="HomelabUptime">{formatUptime(monitor.uptime)} uptime (24h)</p>
        )}
      </div>
    </div>
  );
}

class Homelab extends Component {
  state = {
    loading: true,
    error: false,
    monitors: [],
  };

  componentDidMount() {
    this.loadStatus();
    this.refreshTimer = setInterval(this.loadStatus, REFRESH_MS);
  }

  componentWillUnmount() {
    clearInterval(this.refreshTimer);
    if (this.controller) {
      this.controller.abort();
    }
  }

  loadStatus = async () => {
    if (this.controller) {
      this.controller.abort();
    }
    const controller = new AbortController();
    this.controller = controller;

    try {
      const [statusPage, heartbeats] = await Promise.all([
        fetchJson(STATUS_PAGE_URL, controller.signal),
        fetchJson(HEARTBEAT_URL, controller.signal),
      ]);
      this.setState({
        loading: false,
        error: false,
        monitors: buildMonitors(statusPage, heartbeats),
      });
    } catch (err) {
      if (err.name === "AbortError") {
        return;
      }
      this.setState({ loading: false, error: true, monitors: [] });
    }
  };

  renderStatus() {
    const { loading, error, monitors } = this.state;

    if (loading) {
      return <p className="HomelabMessage">Loading live status...</p>;
    }
    if (error) {
      return (
        <p className="HomelabMessage">
          Live monitoring data is temporarily unavailable.
        </p>
      );
    }

    return (
      <div className="row g-4">
        {monitors.map((monitor) => (
          <div className="col-12 col-sm-6 col-lg-3" key={monitor.id}>
            <SystemStatusCard monitor={monitor} />
          </div>
        ))}
      </div>
    );
  }

  render() {
    return (
      <div className="Page">
        <div className="PageContainer">
          <h1 className="PageHeading">Lab Status</h1>
          <p className="PageIntro">
            Live availability monitoring for selected systems and services in
            my homelab.
          </p>
          <p className="HomelabPoweredBy">Monitoring powered by Uptime Kuma.</p>

          <section
            aria-labelledby="lab-status-heading"
            aria-busy={this.state.loading}
            className="HomelabSection"
          >
            <h2 id="lab-status-heading" className="SectionHeading">
              Live Lab Status
            </h2>
            <div aria-live="polite">{this.renderStatus()}</div>
          </section>
        </div>
      </div>
    );
  }
}

export default Homelab;
